import { useMutation, UseMutationOptions, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

import { MutationOptions, QueryOptions } from '@/app/lib/tanstack-query';
import { getUser, passwordLogin } from '@/app/network/firefly-api';
import { AccessTokenResponse } from '@/app/network/models/access-token-response';
import { CurrentUserResponse } from '@/app/network/models/v1/user';
import { clearTokens, hasTokens, setTokens } from '@/shared/auth/user-store';
import { minutes } from '@/shared/milliseconds';

/**
 * An object containing methods for constructing cache keys
 * related to the "user" domain.
 */
export const userKeys = {
  all: ['user'] as const,
  authenticated: () => [...userKeys.all, 'authenticated'] as const,
  onboardingRequirement: (userId: number) => [...userKeys.all, userId, 'onboardingRequirement']
};

/**
 * Authenticates a user with the provided credentials and retrieves the user's details.
 *
 * @param {Object} credentials - The login credentials for the user.
 * @param {string} credentials.username - The username of the user.
 * @param {string} credentials.password - The password of the user.
 * @return A promise that resolves to an object containing the authentication tokens and user details.
 */
async function loginAndRetrieveUser({ username, password }: LoginCredentials) {
  const tokens = await passwordLogin(username, password);
  setTokens(tokens);

  const user = await getUser();

  return { tokens, user };
}

type LoginCredentials = { username: string; password: string };

/**
 * Log user in and retrieve related user data.
 */
export function useLogin(options?: MutationOptions<{ tokens: AccessTokenResponse; user: CurrentUserResponse }, Error, LoginCredentials>) {
  const queryClient = useQueryClient();

  const { onSuccess, onError, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: loginAndRetrieveUser,
    onSuccess: (data, ...rest) => {
      queryClient.setQueryData(userKeys.authenticated(), data.user);
      onSuccess?.(data, ...rest);
    },
    onError(error, ...rest) {
      clearTokens();
      onError?.(error, ...rest);
    },
    ...restOptions
  });
}

/**
 * Log out a user and clear authentication tokens.
 */
export function useLogout(options?: MutationOptions) {
  const queryClient = useQueryClient();

  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: async () => {
      clearTokens();
    },
    onSuccess: (...args) => {
      queryClient.clear();
      onSuccess?.(...args);
    },
    ...restOptions
  });
}

export function useImpersonate(options?: Omit<UseMutationOptions<void, Error, { accessToken: string }>, 'mutationFn'>) {
  const queryClient = useQueryClient();

  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: async ({ accessToken }) => {
      setTokens({ access_token: accessToken }, true);
    },
    onSuccess: async (...args) => {
      queryClient.clear();
      await queryClient.refetchQueries({ queryKey: userKeys.authenticated() });
      onSuccess?.(...args);
    },
    ...restOptions
  });
}

export function getCurrentUserQueryOptions() {
  return {
    queryKey: userKeys.authenticated(),
    queryFn: getUser,
    enabled: hasTokens(),
    staleTime: minutes(5)
  } satisfies UseQueryOptions<CurrentUserResponse>;
}

/**
 * Access current user information.
 */
export function useUser(options?: QueryOptions<CurrentUserResponse>) {
  return useQuery({
    ...getCurrentUserQueryOptions(),
    ...options
  });
}
